import React, { useEffect, useState } from 'react'
import {
  useCarousel,
  useProductState,
  useProductFunctions,
  VideoType,
} from '@sentecacommerce-theme/lib'
import styled from 'styled-components'
import { LazyResponsiveImage } from '../../../Common'
import { DesktopPreviewCarousel } from '../DesktopPreviewCarousel'
import { IconZoom } from '../../../Common/Icons/IconZoom'

export const ProductCarousel = ({
  onSlideClick,
  initialSlide = 0,
  isInModal,
  imageIndex,
}: {
  onSlideClick?: (index: number) => void
  initialSlide?: number
  isInModal?: boolean
  imageIndex?: number | undefined
}) => {
  const [currentSlide, setCurrentSlide] = useState(initialSlide || 0)

  const { metaData, selectedVariant } = useProductState()
  const { getProductVideos } = useProductFunctions()
  const videos = getProductVideos()

  const [Carousel, carousel] = useCarousel({
    containScroll: 'trimSnaps',
    startIndex: initialSlide || 0,
  })

  const slideToIndex = (index: number) => {
    carousel?.scrollTo(index)
  }

  useEffect(() => {
    carousel?.on('select', () => {
      setCurrentSlide(carousel?.selectedScrollSnap() || 0)
    })
    if (typeof imageIndex === 'number') {
      slideToIndex(imageIndex)
      setCurrentSlide(imageIndex)
    }
  }, [carousel, imageIndex])

  const handleSlideClick = (index: number) => () => {
    if (!carousel?.clickAllowed()) return
    onSlideClick?.(index)
  }

  const firstImage = selectedVariant?.images?.[0]

  const images = [
    firstImage,
    ...(videos as any),
    ...(selectedVariant?.images?.slice(1) ?? []),
  ]

  const scrollPrev = React.useCallback(() => carousel?.scrollPrev(), [carousel])
  const scrollNext = React.useCallback(() => carousel?.scrollNext(), [carousel])

  const onKeyPress = React.useCallback(
    (e: KeyboardEvent) => {
      if (!isInModal) return
      if ((e.key === 'ArrowLeft' || e.key === 'ArrowUp') && currentSlide > 0) {
        scrollPrev()
        return
      } else if (
        (e.key === 'ArrowDown' || e.key === 'ArrowRight') &&
        images.length - 1 > currentSlide
      ) {
        scrollNext()
      }
    },
    [currentSlide, images.length, scrollNext, scrollPrev, isInModal]
  )

  useEffect(() => {
    window.addEventListener('keydown', onKeyPress)

    return () => {
      window.removeEventListener('keydown', onKeyPress)
    }
  }, [onKeyPress])

  return (
    <Container isInModal={isInModal}>
      <PreviewContainer>
        <DesktopPreviewCarousel
          currentSlide={currentSlide}
          slideToIndex={slideToIndex}
          videos={videos}
        />
      </PreviewContainer>
      <SlidesContainer isInModal={isInModal}>
        <Carousel>
          <SlidesWrapper>
            {images?.map((imagesMap, index) => {
              return ((imagesMap as unknown) as VideoType).type === 'video' &&
                index === currentSlide ? (
                ((imagesMap as unknown) as VideoType).iframe
              ) : (
                <Slide onClick={handleSlideClick(index)} key={`image_${index}`}>
                  <LazyResponsiveImage
                    breakpointSizes={
                      isInModal
                        ? {}
                        : {
                            xs: '100vw',
                            sm: '100vw',
                            md: '100vw',
                            lg: '50vw',
                            xl: '660px',
                            xxl: '660px',
                          }
                    }
                    defaultImageWidth="100vw"
                    imagesMap={imagesMap}
                    alt={metaData.name}
                  />
                </Slide>
              )
            })}
          </SlidesWrapper>
        </Carousel>
        {!isInModal && (
          <ZoomIconWrapper>
            <IconZoom />
          </ZoomIconWrapper>
        )}
      </SlidesContainer>
      <DotsContainer>
        {images?.map((_, index) => (
          <Dot
            onClick={() => slideToIndex(index)}
            selected={currentSlide === index}
            key={`Dot__${index}`}
          />
        ))}
      </DotsContainer>
    </Container>
  )
}

const Container = styled.div<{ isInModal?: boolean }>`
  position: relative;
  width: 100%;
  @media (min-width: ${({ theme }) => theme.breakpoints.lg.min}) {
    display: flex;
    align-items: center;
    justify-content: ${(props) =>
      props.isInModal ? 'center' : 'space-between'};
  }
`

const SlidesContainer = styled.div<{ isInModal?: boolean }>`
  margin-bottom: 0;
  position: relative;
  z-index: 1;

  width: ${(props) => (props.isInModal ? 'min(100vh, 100%)' : '100%')};

  @media (min-width: ${({ theme }) => theme.breakpoints.lg.min}) {
    width: ${(props) =>
      props.isInModal ? 'min(100vh, 100%)' : 'calc(100% - 10rem)'};
  }

  &:hover svg {
    opacity: 0.15;
  }
`

const SlidesWrapper = styled.div.attrs({
  className: 'd-flex',
})`
  padding-bottom: 0;
`

const DotsContainer = styled.div.attrs({
  className:
    'row d-lg-none gx-2 justify-content-center align-items-center mt-3',
})`
  height: 2.8rem;
`

const Dot = styled.div.attrs({
  className: 'col-auto',
})<{ selected?: boolean }>`
  margin: 0 0.15rem;
  &:after {
    cursor: pointer;
    content: '';
    display: block;
    border-radius: 50%;
    height: 0.8rem;
    width: 0.8rem;
    box-sizing: border-box;
    background: ${(props) => props.selected && props.theme.palette.silverTree};
    border: 0.15rem solid ${({ theme }) => theme.palette.silverTree};
  }
`

const ZoomIconWrapper = styled.div.attrs({
  className: 'd-none d-lg-block',
})`
  pointer-events: none;
  position: absolute;
  bottom: 1rem;
  right: 1rem;

  & svg {
    width: 2.8rem;
    height: 2.8rem;
    opacity: 0;
  }
`

const Slide = styled.figure.attrs({
  displayName: 'py-0 px-4',
})`
  min-width: calc(100% + 2.8rem);
  max-width: calc(100% + 2.8rem);
  cursor: pointer;
`

const PreviewContainer = styled.div.attrs({
  className: 'd-none d-lg-block pr-3',
})``
